import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { formatMoney } from '../../utils/partnerFinance';
import {
  CHARGE_CATEGORIES,
  SPLIT_METHODS,
  UNIT_BASES,
  centsToAmount,
  chargeFormToPayload,
  chargeToForm,
  emptyChargeForm,
  previewSplit,
} from '../../utils/groupBilling';
import { eyebrowSx, fieldSx } from './BillingParts';

const NIGHTLY = new Set(['per_night_per_unit', 'per_person_per_night']);

const GroupChargeDialog = ({
  open,
  onClose,
  onSave,
  saving = false,
  charge = null,
  template = 'custom',
  travelers = [],
  units = [],
  currency = 'USD',
  error = '',
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => emptyChargeForm(template, currency));
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(charge ? chargeToForm(charge, currency) : emptyChargeForm(template, currency));
    setShowAdvanced(false);
  }, [open, charge, template, currency]);

  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  const unit = units.find((u) => String(u.id) === String(form.inventoryUnitId)) || null;
  const travelerById = useMemo(() => new Map(travelers.map((tr) => [tr.id, tr])), [travelers]);

  const people = useMemo(() => {
    if (unit) {
      return unit.occupants.map((o) => ({
        id: o.travelerId,
        berth: o.berth,
        travelerType: o.travelerType,
        isLead: travelerById.get(o.travelerId)?.isLead,
      }));
    }
    return Object.entries(form.participants)
      .filter(([, p]) => p?.selected)
      .map(([id, p]) => ({
        id: Number(id),
        weight: p.weight,
        fixedAmount: p.fixedAmount,
        travelerType: travelerById.get(Number(id))?.travelerType,
        isLead: travelerById.get(Number(id))?.isLead,
      }));
  }, [unit, form.participants, travelerById]);

  const preview = useMemo(() => previewSplit(form, people), [form, people]);
  const previewById = new Map(preview.rows.map((r) => [r.id, r.amountCents]));

  const toggleTraveler = (id, selected) => {
    setForm((prev) => ({
      ...prev,
      participants: {
        ...prev.participants,
        [id]: { weight: 1, fixedAmount: '', ...(prev.participants[id] || {}), selected },
      },
    }));
  };
  const patchTraveler = (id, next) => {
    setForm((prev) => ({
      ...prev,
      participants: { ...prev.participants, [id]: { ...(prev.participants[id] || {}), ...next } },
    }));
  };
  const selectAll = () => {
    const next = {};
    travelers.forEach((tr) => {
      next[tr.id] = { weight: 1, fixedAmount: '', ...(form.participants[tr.id] || {}), selected: true };
    });
    patch({ participants: next });
  };

  const needsUnit = form.splitMethod === 'occupancy' && !unit;
  const canSave = form.name.trim() && form.unitPrice !== '' && !needsUnit
    && (form.splitMethod !== 'fixed' || preview.fixedMismatchCents === 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="group-charge-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
        {charge ? t('groupBilling.charge.editTitle') : t('groupBilling.charge.addTitle')}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('groupBilling.charge.name')}
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder={t('groupBilling.charge.namePlaceholder')}
              size="small"
              sx={fieldSx}
              inputProps={{ 'data-testid': 'group-charge-name' }}
              required
            />
            <FormControl size="small" sx={fieldSx}>
              <InputLabel>{t('groupBilling.charge.category')}</InputLabel>
              <Select
                label={t('groupBilling.charge.category')}
                value={form.category}
                onChange={(e) => patch({ category: e.target.value })}
              >
                {CHARGE_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{t(`groupBilling.category.${c}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 1fr 1fr 1fr' }, gap: 2 }}>
            <FormControl size="small" sx={fieldSx}>
              <InputLabel>{t('groupBilling.charge.basis')}</InputLabel>
              <Select
                label={t('groupBilling.charge.basis')}
                value={form.unitBasis}
                onChange={(e) => patch({ unitBasis: e.target.value })}
                inputProps={{ 'data-testid': 'group-charge-basis' }}
              >
                {UNIT_BASES.map((b) => (
                  <MenuItem key={b} value={b}>{t(`groupBilling.basis.${b}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('groupBilling.charge.price', { currency: form.currency })}
              type="number"
              size="small"
              sx={fieldSx}
              value={form.unitPrice}
              onChange={(e) => patch({ unitPrice: e.target.value })}
              inputProps={{ min: 0, step: '0.01', 'data-testid': 'group-charge-price' }}
              required
            />
            <TextField
              label={t('groupBilling.charge.quantity')}
              type="number"
              size="small"
              sx={fieldSx}
              value={form.quantity}
              onChange={(e) => patch({ quantity: e.target.value })}
              inputProps={{ min: 1 }}
            />
            {NIGHTLY.has(form.unitBasis) ? (
              <TextField
                label={t('groupBilling.charge.nights')}
                type="number"
                size="small"
                sx={fieldSx}
                value={form.nights}
                onChange={(e) => patch({ nights: e.target.value })}
                inputProps={{ min: 1, 'data-testid': 'group-charge-nights' }}
              />
            ) : <span />}
          </Box>

          <Box>
            <Typography sx={{ ...eyebrowSx, mb: 1 }}>{t('groupBilling.charge.splitTitle')}</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.splitMethod}
              onChange={(e, v) => v && patch({ splitMethod: v })}
              sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { borderRadius: 0, textTransform: 'none' } }}
            >
              {SPLIT_METHODS.map((m) => (
                <ToggleButton key={m} value={m} data-testid={`group-charge-split-${m}`}>
                  {t(`groupBilling.split.${m}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 0.75 }}>
              {t(`groupBilling.split.${form.splitMethod}Hint`)}
            </Typography>
          </Box>

          {(units.length > 0 || form.splitMethod === 'occupancy') && (
            <FormControl size="small" sx={fieldSx}>
              <InputLabel>{t('groupBilling.charge.unit')}</InputLabel>
              <Select
                label={t('groupBilling.charge.unit')}
                value={form.inventoryUnitId}
                onChange={(e) => patch({ inventoryUnitId: e.target.value })}
                inputProps={{ 'data-testid': 'group-charge-unit' }}
              >
                <MenuItem value="">{t('groupBilling.charge.noUnit')}</MenuItem>
                {units.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.label}{u.category ? ` · ${u.category}` : ''} ({u.occupants.length}/{u.capacity})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {needsUnit && <Alert severity="info">{t('groupBilling.charge.occupancyNeedsUnit')}</Alert>}

          {form.splitMethod === 'occupancy' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              {['single', 'double', 'third', 'fourth'].map((k) => (
                <TextField
                  key={k}
                  label={t(`groupBilling.occupancy.${k}`)}
                  type="number"
                  size="small"
                  sx={fieldSx}
                  value={form.occupancyPricing[k]}
                  onChange={(e) => patch({ occupancyPricing: { ...form.occupancyPricing, [k]: e.target.value } })}
                  inputProps={{ min: 0, step: '0.01', 'data-testid': `group-charge-occ-${k}` }}
                />
              ))}
            </Box>
          )}

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={eyebrowSx}>
                {unit ? t('groupBilling.charge.occupants', { unit: unit.label }) : t('groupBilling.charge.whoOwes')}
              </Typography>
              {!unit && travelers.length > 0 && (
                <Button size="small" sx={partnerLinkSx} onClick={selectAll} data-testid="group-charge-select-all">
                  {t('groupBilling.charge.selectAll')}
                </Button>
              )}
            </Box>
            {travelers.length === 0 && (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('groupBilling.charge.noTravelers')}</Typography>
            )}
            {unit && unit.occupants.length === 0 && (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('groupBilling.charge.unitEmpty')}</Typography>
            )}
            <Stack divider={<Box sx={{ borderTop: `1px solid ${DOCUMENT.rule}` }} />}>
              {(unit ? unit.occupants.map((o) => travelerById.get(o.travelerId)).filter(Boolean) : travelers).map((tr) => {
                const p = form.participants[tr.id] || {};
                const selected = unit ? true : Boolean(p.selected);
                const amount = previewById.get(tr.id);
                return (
                  <Box key={tr.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, flexWrap: 'wrap' }}>
                    {unit ? (
                      <Typography sx={{ flex: 1, minWidth: 160, fontSize: 14 }}>{tr.name}</Typography>
                    ) : (
                      <FormControlLabel
                        sx={{ flex: 1, minWidth: 160, m: 0 }}
                        control={(
                          <Checkbox
                            size="small"
                            checked={selected}
                            onChange={(e) => toggleTraveler(tr.id, e.target.checked)}
                            inputProps={{ 'data-testid': `group-charge-traveler-${tr.id}` }}
                          />
                        )}
                        label={(
                          <Typography sx={{ fontSize: 14 }}>
                            {tr.name}
                            {tr.travelerType !== 'adult' ? ` · ${t(`groupBilling.travelerType.${tr.travelerType}`)}` : ''}
                          </Typography>
                        )}
                      />
                    )}
                    {!unit && selected && form.splitMethod === 'weighted' && (
                      <TextField
                        size="small"
                        type="number"
                        label={t('groupBilling.charge.weight')}
                        value={p.weight ?? 1}
                        onChange={(e) => patchTraveler(tr.id, { weight: e.target.value })}
                        sx={{ ...fieldSx, width: 110 }}
                        inputProps={{ min: 0, step: '0.1', 'data-testid': `group-charge-weight-${tr.id}` }}
                      />
                    )}
                    {!unit && selected && form.splitMethod === 'fixed' && (
                      <TextField
                        size="small"
                        type="number"
                        label={t('groupBilling.charge.fixedAmount')}
                        value={p.fixedAmount ?? ''}
                        onChange={(e) => patchTraveler(tr.id, { fixedAmount: e.target.value })}
                        sx={{ ...fieldSx, width: 130 }}
                        inputProps={{ min: 0, step: '0.01', 'data-testid': `group-charge-fixed-${tr.id}` }}
                      />
                    )}
                    <Typography
                      sx={{ minWidth: 110, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: selected ? DOCUMENT.ink : DOCUMENT.ink3 }}
                      data-testid={`group-charge-preview-${tr.id}`}
                    >
                      {selected && amount !== undefined ? formatMoney(centsToAmount(amount), currency) : '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            {form.splitMethod === 'weighted' && !unit && (
              <Button
                size="small"
                sx={{ ...partnerLinkSx, mt: 1 }}
                onClick={() => {
                  const next = { ...form.participants };
                  travelers.forEach((tr) => {
                    if (next[tr.id]?.selected && tr.travelerType !== 'adult') next[tr.id] = { ...next[tr.id], weight: 0.5 };
                  });
                  patch({ participants: next });
                }}
              >
                {t('groupBilling.charge.kidsHalf')}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: `1px solid ${DOCUMENT.ruleHard}`, pt: 1.5 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{t('groupBilling.charge.total')}</Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22 }} data-testid="group-charge-total">
              {formatMoney(centsToAmount(preview.totalCents), currency)}
            </Typography>
          </Box>
          {form.splitMethod === 'fixed' && preview.fixedMismatchCents !== 0 && (
            <Alert severity="warning" data-testid="group-charge-fixed-mismatch">
              {t('groupBilling.charge.fixedMismatch', { amount: formatMoney(centsToAmount(preview.fixedMismatchCents), currency) })}
            </Alert>
          )}

          <Button size="small" sx={{ ...partnerLinkSx, alignSelf: 'flex-start' }} onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? t('groupBilling.charge.hideAdvanced') : t('groupBilling.charge.showAdvanced')}
          </Button>
          <Collapse in={showAdvanced}>
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <TextField label={t('groupBilling.charge.netCost')} type="number" size="small" sx={fieldSx}
                  value={form.unitCost} onChange={(e) => patch({ unitCost: e.target.value })}
                  helperText={t('groupBilling.charge.netCostHint')} />
                <TextField label={t('groupBilling.charge.supplier')} size="small" sx={fieldSx}
                  value={form.supplier} onChange={(e) => patch({ supplier: e.target.value })} />
                <TextField label={t('groupBilling.charge.commissionRate')} type="number" size="small" sx={fieldSx}
                  value={form.commissionRate} onChange={(e) => patch({ commissionRate: e.target.value })}
                  disabled={!form.commissionable} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <TextField label={t('groupBilling.charge.currency')} size="small" sx={fieldSx}
                  value={form.currency} onChange={(e) => patch({ currency: e.target.value.toUpperCase().slice(0, 3) })} />
                <TextField label={t('groupBilling.charge.fxRate', { currency })} type="number" size="small" sx={fieldSx}
                  value={form.fxRate} onChange={(e) => patch({ fxRate: e.target.value })} inputProps={{ step: '0.0001' }} />
                <TextField label={t('groupBilling.charge.dueDate')} type="date" size="small" sx={fieldSx}
                  InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={(e) => patch({ dueDate: e.target.value })} />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <FormControlLabel control={<Switch checked={Boolean(form.commissionable)} onChange={(e) => patch({ commissionable: e.target.checked })} />}
                  label={t('groupBilling.charge.commissionable')} />
                <FormControlLabel control={<Switch checked={Boolean(form.refundable)} onChange={(e) => patch({ refundable: e.target.checked })} />}
                  label={t('groupBilling.charge.refundable')} />
                <FormControlLabel control={<Switch checked={Boolean(form.dueInFull)} onChange={(e) => patch({ dueInFull: e.target.checked })} inputProps={{ 'data-testid': 'group-charge-due-in-full' }} />}
                  label={t('groupBilling.charge.dueInFull')} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label={t('groupBilling.charge.penaltyAfter')} type="date" size="small" sx={fieldSx}
                  InputLabelProps={{ shrink: true }} value={form.penaltyAfter} onChange={(e) => patch({ penaltyAfter: e.target.value })} />
                <TextField label={t('groupBilling.charge.penaltyAmount')} type="number" size="small" sx={fieldSx}
                  value={form.penaltyAmount} onChange={(e) => patch({ penaltyAmount: e.target.value })}
                  helperText={t('groupBilling.charge.penaltyHint')} />
              </Box>
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${DOCUMENT.rule}` }}>
        <Button onClick={onClose} sx={partnerLinkSx}>{t('common.cancel')}</Button>
        <Button
          onClick={() => onSave(chargeFormToPayload(form))}
          disabled={saving || !canSave}
          sx={partnerOxbloodBtnSx}
          data-testid="group-charge-save"
        >
          {saving ? <CircularProgress size={20} sx={{ color: DOCUMENT.ivory }} /> : t('groupBilling.charge.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupChargeDialog;
